import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import SignatureLogo from "../../../common/icons/signatureLogo";
import PlusIcon from "../../../common/icons/plusIcon";
import Button from "../../../common/forms/button";
import { clickToLinkHandler } from "../../../../helpers/utils";
import * as api from "../../../../api/generated-api";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../../api/axiosConfig";
import { isCompanyMemberOrCounselForDeal } from "../../../../helpers/utilsTS";
import { getDealById } from "../../../../api/deals/deals";
import { getCompanyByOrgId } from "../../../../api/company/company";
import { getDealDocumentSignatures } from "../../../../api/deals/signatures";
import {
  DealInvestorsLegalEntitiesMap,
  getDealInvestors,
  getDealInvestorsLegalEntitiesForInvestorsAsync,
} from "../../../../api/deals/investors";
import { getDealCompanyMembers } from "../../../../api/deals/companyMembers";
import { getDealDocuments } from "../../../../api/deals/documents";
import { DealsPageLayout } from "../../dealsPageLayout/DealsPageLayout";
import SignaturesRequestPanel from "./SignaturesRequestPanel/SignaturesRequestPanel";
import {
  getDealLegalEntitiesForDeal,
  getDealLegalEntitiesForUser,
} from "../../../../api/deals/deallegalentities";
import InvestorSignatureGroup from "./InvestorSignatureGroup";
import CompanySignatureGroup from "./CompanySignatureGroup";
import { Box } from "@mui/material";

type SignaturesPagePropTypes = {
  orgId: number;
  dealId: number;
  user: api.User & {
    person: api.Person & {
      organizations_persons: [
        {
          organization_id?: number;
          person_id: number;
          role_id: number;
          organization?: api.Organization;
        }
      ];
    };
  };
  token: string;
};

const SignatureAndWireTrackerPage = (props) => {
  const { orgId, dealId, user, token }: SignaturesPagePropTypes = props;

  configureBaseUrl();
  configureAuthHeader(token);

  type DealCompanyMemberSigners = {
    category: string;
    dealLegalEntities: api.DealLegalEntity[];
  };

  const [deal, setDeal] = useState<api.Deal>(null);
  const [company, setCompany] = useState<api.Company>(null);
  const [dealDocumentSignatures, setDealDocumentSignatures] = useState<
    api.DealDocumentSignature[]
  >([]);
  const [dealInvestors, setDealInvestors] = useState<api.DealInvestor[]>();
  const [
    dealLegalEntitiesForCompanyRoles,
    setDealLegalEntitiesForCompanyRoles,
  ] = useState<api.DealLegalEntity[]>([]);
  const [
    dealLegalEntityInvestmentsByInvestor,
    setDealLegalEntityInvestmentsByInvestor,
  ] = useState<DealInvestorsLegalEntitiesMap>(new Map());
  const [dealLegalEntities, setDealLegalEntities] = useState<
    api.DealLegalEntity[]
  >([]);
  const [dealDocuments, setDealDocuments] = useState<api.DealDocument[]>([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [
    groupedDealLegalEntitiesForCompany,
    setGroupedDealLegalEntitiesForCompany,
  ] = useState<DealCompanyMemberSigners[]>();
  const [dealLegalEntitiesForUser, setDealLegalEntitiesForUser] = useState<
    api.DealLegalEntity[]
  >([]);
  const [signaturePanelDealLegalEntityId, setSignaturePanelDealLegalEntityId] =
    useState<number>(null);

  useEffect(() => {
    getDealLegalEntitiesForUser(orgId, dealId, (fetchedDealLegalEntities) => {
      setDealLegalEntitiesForUser(fetchedDealLegalEntities);
    });
  }, [orgId, dealId]);

  useEffect(() => {
    //get initial data from APIs and update in state

    getDealById(orgId, dealId, (deal) => {
      setDeal(deal);
    });

    getCompanyByOrgId(orgId, (company) => {
      setCompany(company);
    });

    getDealDocumentSignatures(orgId, dealId, (dealDocumentSignatures) => {
      setDealDocumentSignatures(dealDocumentSignatures);
    });

    getDealLegalEntitiesForDeal(orgId, dealId, (dealLegalEntities) => {
      setDealLegalEntities(dealLegalEntities);
    });

    getDealInvestors(orgId, dealId, (dealInvestors) => {
      setDealInvestors(dealInvestors);

      const dealInvestorIds = dealInvestors.map(
        (dealInvestor) => dealInvestor.id
      );

      const getDealLegalEntityInvestmentsForInvestor = async () => {
        const updatedDealInvestmentsByDealLegalEntityMap =
          await getDealInvestorsLegalEntitiesForInvestorsAsync(
            orgId,
            dealId,
            dealInvestorIds
          );

        setDealLegalEntityInvestmentsByInvestor(
          updatedDealInvestmentsByDealLegalEntityMap
        );
      };

      getDealLegalEntityInvestmentsForInvestor();
    });

    getDealCompanyMembers(orgId, dealId, (dealLegalEntitiesForCompanyRoles) => {
      setDealLegalEntitiesForCompanyRoles(dealLegalEntitiesForCompanyRoles);
    });

    getDealDocuments(orgId, dealId, (fetchedDealDocuments) => {
      setDealDocuments(fetchedDealDocuments);
    });
  }, []);

  useEffect(() => {
    //group deal legal entities for company roles by role
    const groupedDealLegalEntitiesForCompany =
      dealLegalEntitiesForCompanyRoles.reduce(
        (
          accumulator: {
            category: string;
            dealLegalEntities: (typeof dealLegalEntity)[];
          }[],
          dealLegalEntity
        ) => {
          //get the category name based on legal entity type
          const getCategoryNameForPosition = (
            position: api.LegalEntityType
          ) => {
            if (
              position === api.LegalEntityType.Chief_Executive_Officer ||
              position === api.LegalEntityType.Secretary
            ) {
              return "Officers";
            } else if (position === api.LegalEntityType.Company_Counsel) {
              return "Company Counsel";
            } else {
              return position + "s";
            }
          };

          //check if category already exists in accumulator
          const existingCategoryIndex = accumulator.findIndex(
            (acc) =>
              acc.category ===
              getCategoryNameForPosition(dealLegalEntity.legalEntityType)
          );

          if (existingCategoryIndex > -1) {
            //if the category already exists in the accumulator, add the deal company member
            accumulator[existingCategoryIndex].dealLegalEntities.push(
              dealLegalEntity
            );
          } else {
            //if the category doesn't already exist in the accumulator, add a new category along with the deal company member
            accumulator.push({
              category: getCategoryNameForPosition(
                dealLegalEntity.legalEntityType
              ),
              dealLegalEntities: [dealLegalEntity],
            });
          }

          return accumulator;
        },
        []
      );

    setGroupedDealLegalEntitiesForCompany(groupedDealLegalEntitiesForCompany);
  }, [dealLegalEntitiesForCompanyRoles, dealDocumentSignatures]);

  const handleOnWireDateChange = async (
    investorId: number,
    dealLegalEntityInvestmentId: number,
    wireDate: string
  ) => {
    // make a copy of the Map
    const updatedDealLegalEntityInvestmentsByInvestor = new Map(
      dealLegalEntityInvestmentsByInvestor
    );

    // get the deal legal entity investments for the investor
    const updatedDealLegalEntityInvestmentsForInvestor =
      updatedDealLegalEntityInvestmentsByInvestor.get(investorId);

    // within the investments, find the one that matches the one being updated
    const legalEntityIndex =
      updatedDealLegalEntityInvestmentsForInvestor.findIndex(
        (dealLegalEntityInvestment) =>
          dealLegalEntityInvestment.id === dealLegalEntityInvestmentId
      );

    if (legalEntityIndex > -1) {
      // update the wire date for the investment
      updatedDealLegalEntityInvestmentsForInvestor[
        legalEntityIndex
      ].wireDateReceived = wireDate;

      // update the Map with the updated investments
      updatedDealLegalEntityInvestmentsByInvestor.set(
        investorId,
        updatedDealLegalEntityInvestmentsForInvestor
      );

      // update state
      setDealLegalEntityInvestmentsByInvestor(
        updatedDealLegalEntityInvestmentsByInvestor
      );
    }
  };

  const handleSendSignatureRequest = async (
    signaturesToBeRequested: api.DealDocumentSignature[],
    isReminder = false
  ) => {
    //get the existing deal document signers state value in case of rollback
    const existingDealDocumentSignatures = [...dealDocumentSignatures];

    //optimistically update deal document signatures in state
    const updatedDealDocumentSignatures = [...dealDocumentSignatures];

    signaturesToBeRequested.forEach((dealDocumentSigner) => {
      const index = updatedDealDocumentSignatures.findIndex(
        (item) => item.id === dealDocumentSigner.id
      );
      if (index > -1) {
        updatedDealDocumentSignatures[index].status = "requested";
      }
    });

    setDealDocumentSignatures(updatedDealDocumentSignatures);

    //call API to send signature request
    const newSignatureRequest: api.DealSignatureRequest = {
      dealLegalEntityId: signaturesToBeRequested[0].dealLegalEntityId,
    };
    const documentIdsFilter = signaturesToBeRequested
      .map((dealDocumentSigner) => dealDocumentSigner.dealDocumentId)
      //Use Reduce to remove any duplicate document ids.
      .reduce((noDupes, current) => {
        if (!noDupes.includes(current)) {
          noDupes.push(current);
        }
        return noDupes;
      }, []);

    const params: api.SendDealSignatureRequestParams = {
      documentIds: documentIdsFilter.toString(),
      reminder: isReminder,
    };

    const result = await api.sendDealSignatureRequest(
      orgId,
      dealId,
      newSignatureRequest,
      params
    );

    if (result.status !== 201) {
      //rollback state on error
      setDealDocumentSignatures(existingDealDocumentSignatures);
    }
  };

  if (!dealLegalEntitiesForUser) {
    return null;
  }

  const isUserCompanyMember = isCompanyMemberOrCounselForDeal(
    user.person.organizations_persons,
    orgId,
    dealId,
    dealLegalEntitiesForUser
  );

  if (deal && !dealInvestors && !groupedDealLegalEntitiesForCompany) {
    return (
      <Box>
        <Box>
          <SignatureLogo />
          <Box>
            <p>Use this page to request and track document signatures and</p>
            <p>
              update wire transfers.{" "}
              <strong>But first you need to add an investor.</strong>
            </p>
          </Box>
        </Box>
        <Button
          type="secondary"
          size="medium"
          onClick={() => {
            clickToLinkHandler(
              "/" + orgId + "/deals/" + deal.id + "/edit/investors"
            );
          }}
        >
          <Box>
            <PlusIcon />
            add investor to deal
          </Box>
        </Button>
      </Box>
    );
  }

  return (
    <DealsPageLayout
      showDealNav={!signaturePanelDealLegalEntityId}
      title="Signatures and Wire Tracker"
      rightHeaderAction={
        <Box>
          {dealDocumentSignatures && (
            //display buttons to filter by deal role (ex. investor)
            <Box
              style={{
                minWidth: "600px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {[
                "all",
                "investors",
                "officers",
                "board members",
                "key common holders",
              ].map((filter, key) => {
                return (
                  <Box
                    style={{
                      marginLeft: "10px",
                    }}
                    key={key}
                  >
                    <Button
                      type={activeFilter === filter ? "primary" : "secondary"}
                      size="medium"
                      onClick={() => {
                        setActiveFilter(filter);
                      }}
                    >
                      {filter}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      }
      sidebar={
        signaturePanelDealLegalEntityId ? (
          <SignaturesRequestPanel
            orgId={orgId}
            dealId={dealId}
            label={
              dealLegalEntities.find(
                (dealLegalEntity) =>
                  dealLegalEntity.id === signaturePanelDealLegalEntityId
              )?.name
            }
            canEdit={isUserCompanyMember}
            signatures={dealDocumentSignatures.filter(
              (dealDocumentSignature) =>
                dealDocumentSignature.dealLegalEntityId ===
                signaturePanelDealLegalEntityId
            )}
            documents={dealDocuments}
            onRequest={handleSendSignatureRequest}
            onClose={() => {
              setSignaturePanelDealLegalEntityId(null);
            }}
          />
        ) : null
      }
    >
      <Box>
        {/* display investor member signers */}
        {(activeFilter === "all" || activeFilter === "investors") &&
          dealInvestors &&
          dealInvestors.map((investor, key) => {
            const dealLegalEntitiesForRow = dealLegalEntities.filter(
              (dealLegalEntity) =>
                dealLegalEntity.legalEntityOrganizationId ===
                  investor.investorOrganizationId &&
                dealLegalEntity.legalEntityType ===
                  api.InvestorLegalEntityType.Investor_Investment
            );

            const dealLegalEntityInvestmentsForRow =
              dealLegalEntityInvestmentsByInvestor.get(investor.id);

            return (
              <InvestorSignatureGroup
                key={key}
                isUserCompanyMember={isUserCompanyMember}
                title={investor.name}
                isLead={investor.isLead}
                dealLegalEntitiesForInvestor={dealLegalEntitiesForRow}
                dealLegalEntityInvestmentsForInvestor={
                  dealLegalEntityInvestmentsForRow
                }
                dealDocumentSignatures={dealDocumentSignatures}
                onOpenSignatureRequestPanel={(dealLegalEntityId) => {
                  setSignaturePanelDealLegalEntityId(dealLegalEntityId);
                }}
                onWireDateChange={(dealLegalEntityInvestmentId, wireDate) => {
                  handleOnWireDateChange(
                    investor.id,
                    dealLegalEntityInvestmentId,
                    wireDate
                  );
                }}
              />
            );
          })}
        {/* display company member signers */}
        {groupedDealLegalEntitiesForCompany &&
          groupedDealLegalEntitiesForCompany
            .filter((dealCompanyMemberSigner) => {
              if (activeFilter === "all") {
                return true;
              } else {
                return (
                  dealCompanyMemberSigner.category.toLowerCase() ===
                  activeFilter.toLowerCase()
                );
              }
            })
            .map((category, key) => {
              return (
                <CompanySignatureGroup
                  key={key}
                  isUserCompanyMember={isUserCompanyMember}
                  title={`${company?.name ? company.name : ""} ${
                    category.category
                  }`}
                  dealLegalEntitiesForGroup={category.dealLegalEntities}
                  dealDocumentSignatures={dealDocumentSignatures}
                  onOpenSignatureRequestPanel={(dealLegalEntityId) => {
                    setSignaturePanelDealLegalEntityId(dealLegalEntityId);
                  }}
                />
              );
            })}
      </Box>
    </DealsPageLayout>
  );
};

const mapStateToProps = (state, props) => {
  const {
    match: {
      params: { dealId, orgId },
    },
  } = props;
  const { user, token } = state.auth;

  return {
    user,
    token,
    orgId,
    dealId,
  };
};

export default connect(mapStateToProps)(SignatureAndWireTrackerPage);
